/**
 * `GET /api/firm/public` — the ONE unauthenticated firm-scoped read
 * (Phase 3B Task 1; spec §2.2).
 *
 * The login page has to render "Sign in with Microsoft" or "Sign in with
 * Google" before anybody is signed in, and since A8 the provider is a
 * per-firm fact (`FirmWorkspaceConnection.provider`), not a build-time
 * constant. Every other route that knows the provider is behind
 * `authenticate`, so this is the page's only source.
 *
 * Its own module rather than a member of `lib/api/admin-workspace.ts`:
 * that one's every route is `requireRole(ADMIN)` and its query key is
 * invalidated by admin writes. This read is public, cached for the life of
 * the login page, and must not be pulled into an ADMIN page's invalidation
 * fan-out.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { FirmPublicDto } from '@ziro/legal-shared-types';
import { apiClient } from './client';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export const FIRM_PUBLIC_QUERY_KEY = ['firm-public'] as const;

const FIRM_PUBLIC_PATH = '/api/firm/public';

export async function fetchFirmPublic(): Promise<FirmPublicDto> {
  const res = await apiClient.get<ApiResponse<FirmPublicDto>>(FIRM_PUBLIC_PATH);
  return res.data;
}

/**
 * `retry: false` — the login page has a defined answer for "the lookup
 * failed" (a provider-neutral button that lets the API decide), and
 * retrying three times in front of a signed-out user only delays it.
 * The firm's provider does not change while a login page is open, so the
 * result is effectively immutable for this mount.
 */
export function useFirmPublic(): UseQueryResult<FirmPublicDto> {
  return useQuery({
    queryKey: FIRM_PUBLIC_QUERY_KEY,
    queryFn: fetchFirmPublic,
    retry: false,
    staleTime: 5 * 60_000,
  });
}
